import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CloseButton from '../../atoms/CloseButton';
import ProfilePicturePreviewModal from '../../molecules/ProfilePicturePreviewModal';
import ProfileEditorialView from '../HomeEditorial/ProfileEditorialView';
import { MobileHeaderSlotContext } from '../MainPage/MobileNavigation/MobileHeaderSlotContext';
import Notification from '../Notifications';
import PublicAuthAction, { PUBLIC_AUTH_ACTION_HREF } from '../../molecules/PublicAuthAction';
import { useProfilePicture } from '../../../hooks/useProfilePicture';
import { useProfileCover } from '../../../hooks/useProfileCover';
import useUserProfileSummary from '../../../hooks/useUserProfileSummary';
import useUserBands from '../../../hooks/useUserBands';
import useProfileShare from '../../../hooks/useProfileShare';
import useProfileMediaLibrary from '../../../hooks/useProfileMediaLibrary';
import useProfileTrackPlayback from '../../../hooks/useProfileTrackPlayback';
import useProfileContactActions from '../../../hooks/useProfileContactActions';
import useUserPresence from '../../../hooks/useUserPresence';
import useUserFollowStatus from '../../../hooks/useUserFollowStatus';
import friendshipService from '../../../services/friendshipService';
import userService from '../../../services/userService';
import { userProfileService } from '../../../services/userProfileService';
import { timelineService } from '../../../services/api/timeline';
import classNames from '../../../utils/classNames';
import { formatLastSeenRelative } from '../../../utils/presenceUtils';
import { normalizeProfileUserId } from '../../../utils/profileRoutes';
import { canCurrentViewerFollowUser } from '../../../utils/userFollowStatus';
import { isPostTimelineItem } from '../../../utils/timelineItems';
import { ConnectIcon, FollowIcon, MessageIcon } from '../../atoms/icons/FriendshipActionIcons';
import {
  applyTrackPlaysCountByIdentity,
  buildProfileEditorialTabs,
  buildProfileEditorialViewModel,
  createProfileEditorialSectionIds,
  normalizeTrackPlaysCount,
} from '../../../utils/profileEditorial';
import {
  PROFILE_PREFERRED_STYLE_EMPTY_LABEL,
  PROFILE_PREFERRED_STYLE_INPUT_PLACEHOLDER,
} from '../../../constants/profile';
import {
  isAbsoluteHttpUrl,
  isProfilePictureMediaUrl,
  toPublicProfileCoverUrl,
  toPublicProfilePictureUrl,
} from '../../../utils/mediaUrl';

import './index.scss';

const noop = () => {};
const PRESENCE_STATUSES = new Set(['online', 'offline', 'away']);
const HERO_PRESENCE_STATUSES = new Set(['online', 'offline', 'away', 'unknown']);
const PROFILE_POST_TIMELINE_OPTIONS = Object.freeze({
  includeEntityItems: false,
  requirePostSource: true,
});
const EMPTY_CONNECTION_PAGE = Object.freeze({
  items: [],
  hasMore: false,
  limit: null,
  offset: 0,
  nextOffset: null,
  totalCount: 0,
});

const getMediaUrlValue = (value) => (
  typeof value === 'string' ? value : value?.url || null
);

const normalizePresenceSnapshot = (status) => {
  if (!status || typeof status !== 'object') {
    return null;
  }
  const rawStatus = status.status
    ?? (typeof status.online === 'boolean' ? (status.online ? 'online' : 'offline') : null);
  const normalizedStatus = rawStatus ? String(rawStatus).trim().toLowerCase() : null;
  if (!PRESENCE_STATUSES.has(normalizedStatus)) {
    return null;
  }
  return {
    status: normalizedStatus,
    lastSeen: normalizedStatus === 'online' ? null : (status.lastSeen ?? null),
  };
};

const normalizeHeroPresenceStatus = (status) => {
  const normalizedStatus = typeof status === 'string' ? status.trim().toLowerCase() : null;
  return HERO_PRESENCE_STATUSES.has(normalizedStatus) ? normalizedStatus : 'unknown';
};

const formatHeroPresenceLabel = ({
  status,
  lastSeen,
  isLoading,
  locale,
  onlineLabel,
  t,
}) => {
  if (status === 'online') {
    return onlineLabel;
  }
  if (status === 'away') {
    return t('presence.status.away', 'Away');
  }
  if (isLoading && status === 'unknown') {
    return t('presence.status.checking', 'Checking status');
  }
  if (status === 'offline') {
    const relative = formatLastSeenRelative(lastSeen, { locale });
    if (relative) {
      return t('presence.status.lastSeen', {
        relative,
        defaultValue: `Last seen ${relative}`,
      });
    }
    return t('presence.status.offline', 'Offline');
  }
  return t('presence.status.unknown', 'Status unavailable');
};

export default function ProfileOverview({
  userId,
  profileUsername = null,
  onClose = noop,
  viewerIdOverride,
  standalone = false,
}) {
  const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal') : null;
  const authViewerId = useSelector((state) => state?.auth?.user?.id ?? null);
  const normalizedProfileUserId = useMemo(() => normalizeProfileUserId(userId), [userId]);
  const normalizedAuthViewerId = useMemo(() => normalizeProfileUserId(authViewerId), [authViewerId]);
  const viewerId = viewerIdOverride === undefined ? authViewerId : viewerIdOverride;
  const normalizedViewerId = useMemo(() => normalizeProfileUserId(viewerId), [viewerId]);
  const isAuthenticatedSelfProfile = Boolean(
    normalizedProfileUserId
    && normalizedAuthViewerId
    && normalizedProfileUserId === normalizedAuthViewerId
  );
  const isPublicPreview = viewerIdOverride === null;
  const timelineAudience = isPublicPreview ? 'public' : 'viewer';
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language || 'en';
  const sectionIds = useMemo(
    () => createProfileEditorialSectionIds(`profile-overview-${userId || 'profile'}`),
    [userId]
  );
  const [activeSection, setActiveSection] = useState(sectionIds.INFO);
  const [posts, setPosts] = useState([]);
  const postsRef = useRef([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const postsTimelineRequestRef = useRef(0);
  const postsTimelineLoadRef = useRef(null);
  const postsTimelineUserIdRef = useRef(null);
  const postsTimelineAudienceRef = useRef(null);
  const [directoryUser, setDirectoryUser] = useState(null);
  const [isDirectoryUserLookupSettled, setIsDirectoryUserLookupSettled] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendsLoaded, setFriendsLoaded] = useState(false);
  const friendsRequestRef = useRef(0);
  const [followers, setFollowers] = useState([]);
  const [followersLoaded, setFollowersLoaded] = useState(false);
  const [followersRefreshNonce, setFollowersRefreshNonce] = useState(0);
  const followersRequestRef = useRef(0);
  const [isFollowActionInFlight, setIsFollowActionInFlight] = useState(false);
  const [mediaToast, setMediaToast] = useState(null);
  const [publicProfilePictureUrl, setPublicProfilePictureUrl] = useState(null);
  const [publicProfileCoverUrl, setPublicProfileCoverUrl] = useState(null);
  const [isPublicProfilePictureLookupSettled, setIsPublicProfilePictureLookupSettled] = useState(false);
  const [isPublicProfileCoverLookupSettled, setIsPublicProfileCoverLookupSettled] = useState(false);
  const followTargetRef = useRef({
    userId: normalizedProfileUserId,
    viewerId: normalizedViewerId,
    isPublicPreview,
  });
  followTargetRef.current = {
    userId: normalizedProfileUserId,
    viewerId: normalizedViewerId,
    isPublicPreview,
  };
  useEffect(() => {
    setActiveSection(sectionIds.INFO);
  }, [sectionIds.INFO]);

  useEffect(() => {
    setIsFollowActionInFlight(false);
  }, [isPublicPreview, normalizedProfileUserId, normalizedViewerId]);

  const syncPosts = useCallback((nextValue) => {
    const nextPosts = typeof nextValue === 'function'
      ? nextValue(postsRef.current)
      : nextValue;
    postsRef.current = Array.isArray(nextPosts) ? nextPosts : [];
    setPosts(postsRef.current);
    return postsRef.current;
  }, []);

  const {
    showProfilePicturePreview,
    setShowProfilePicturePreview,
    profilePicture,
    shouldUseSupportAvatar,
  } = useProfilePicture(userId, { enabled: !isPublicPreview });
  const { profileCover } = useProfileCover(userId, { enabled: !isPublicPreview });

  const {
    profile: profileSummary,
    error: profileError,
    setProfile: setProfileSummary,
  } = useUserProfileSummary(userId, { source: isPublicPreview ? 'internet' : 'public' });
  const isFollowSelfProfile = Boolean(
    normalizedProfileUserId
    && normalizedViewerId
    && normalizedProfileUserId === normalizedViewerId
  );
  const canFollowProfileTarget = canCurrentViewerFollowUser({
    targetUserId: normalizedProfileUserId,
    viewerUserId: normalizedViewerId,
    currentUserCanFollow: profileSummary?.currentUserCanFollow,
    visibility: profileSummary?.visibility,
    privacy: profileSummary?.privacy,
  });
  const canLoadProfileFollowers = Boolean(
    !isPublicPreview
    && normalizedProfileUserId
    && (isFollowSelfProfile || canFollowProfileTarget)
  );
  const isProfileFollowerListResolutionPending = Boolean(
    !isPublicPreview
    && normalizedProfileUserId
    && !isFollowSelfProfile
    && !profileSummary
    && !profileError
  );
  const {
    followStatus,
    applyFollowStatus,
  } = useUserFollowStatus(normalizedProfileUserId, {
    enabled: Boolean(!isPublicPreview && (canFollowProfileTarget || isFollowSelfProfile)),
    logLabel: 'profile follow status',
  });
  const profileSummaryUsername = profileSummary?.username ?? null;
  const shouldResolveDirectoryUsername = Boolean(userId && !profileUsername && !profileSummaryUsername);

  useEffect(() => {
    setDirectoryUser(null);
    setIsDirectoryUserLookupSettled(false);
    if (!shouldResolveDirectoryUsername) {
      return undefined;
    }

    let isCancelled = false;
    userService.fetchUserById(userId)
      .then((user) => {
        if (!isCancelled) {
          setDirectoryUser(user ?? null);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.warn('Failed to resolve profile username for share URL.', error);
          setDirectoryUser(null);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsDirectoryUserLookupSettled(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [shouldResolveDirectoryUsername, userId]);

  const isProfileShareLookupPending = shouldResolveDirectoryUsername && !isDirectoryUserLookupSettled;
  const resolvedUsername = profileSummaryUsername ?? profileUsername ?? directoryUser?.username ?? null;
  const profileShareIdentity = useMemo(() => ({
    username: resolvedUsername,
    userId: isProfileShareLookupPending ? null : userId,
  }), [isProfileShareLookupPending, resolvedUsername, userId]);
  const {
    shareProfile,
    profileShareToast,
    clearProfileShareToast,
  } = useProfileShare(profileShareIdentity);
  const profileShareAction = isProfileShareLookupPending ? null : shareProfile;

  useEffect(() => {
    setPublicProfilePictureUrl(null);
    setPublicProfileCoverUrl(null);
    setIsPublicProfilePictureLookupSettled(false);
    setIsPublicProfileCoverLookupSettled(false);
    if (!isPublicPreview || !userId) {
      return undefined;
    }

    let isCancelled = false;
    Promise.allSettled([
      userProfileService.getPublicProfilePictureUrl(userId),
      userProfileService.getPublicProfileCoverUrl(userId),
    ]).then(([pictureResult, coverResult]) => {
      if (isCancelled) {
        return;
      }
      if (pictureResult.status === 'fulfilled') {
        setPublicProfilePictureUrl(getMediaUrlValue(pictureResult.value));
      } else {
        console.warn('Failed to resolve public profile picture URL.', pictureResult.reason);
        setPublicProfilePictureUrl(null);
      }
      if (coverResult.status === 'fulfilled') {
        setPublicProfileCoverUrl(getMediaUrlValue(coverResult.value));
      } else {
        console.warn('Failed to resolve public profile cover URL.', coverResult.reason);
        setPublicProfileCoverUrl(null);
      }
      setIsPublicProfilePictureLookupSettled(true);
      setIsPublicProfileCoverLookupSettled(true);
    });

    return () => {
      isCancelled = true;
    };
  }, [isPublicPreview, userId]);

  const initialPresence = useMemo(() => {
    const profilePresence = normalizePresenceSnapshot(profileSummary?.status);
    if (profilePresence) {
      return profilePresence;
    }
    if (!isPublicPreview && isAuthenticatedSelfProfile) {
      return { status: 'online', lastSeen: null };
    }
    return { status: 'unknown', lastSeen: null };
  }, [isAuthenticatedSelfProfile, isPublicPreview, profileSummary?.status]);
  const presence = useUserPresence(normalizedProfileUserId, {
    enabled: Boolean(normalizedProfileUserId && !isPublicPreview),
    initialStatus: initialPresence.status,
    initialLastSeen: initialPresence.lastSeen,
  });
  const heroPresenceStatus = isPublicPreview ? null : normalizeHeroPresenceStatus(presence.status);
  const isProfileOnline = heroPresenceStatus === 'online';
  const heroOnlineLabel = t('home.editorial.hero.onlineNow', 'Online now');
  const heroPresenceLabel = heroPresenceStatus ? formatHeroPresenceLabel({
    status: heroPresenceStatus,
    lastSeen: presence.lastSeen,
    isLoading: presence.isLoading,
    locale,
    onlineLabel: heroOnlineLabel,
    t,
  }) : null;
  const {
    isSelfProfile: isContactSelfProfile,
    friendshipRelationship,
    isConnectInFlight,
    isMessageInFlight,
    isConnectDisabled,
    isMessageDisabled,
    handleConnectClick,
    handleMessageClick,
  } = useProfileContactActions({
    targetUserId: userId,
    viewerUserId: isPublicPreview ? null : viewerId,
    onDirectMessageReady: onClose,
    enabled: Boolean(userId && viewerId && !isPublicPreview),
  });

  const bandMembershipAudience = isPublicPreview ? 'public' : 'viewer';
  const canLoadBandMemberships = Boolean(userId && (isPublicPreview || viewerId));
  const {
    bands: bandMemberships,
    isLoading: isBandMembershipLoading,
    hasLoadedSuccessfully: hasLoadedBandMemberships,
  } = useUserBands(userId, {
    audience: bandMembershipAudience,
    enabled: canLoadBandMemberships,
  });

  const {
    profileTracks,
    setProfileTracks,
    userMediaPhotos,
  } = useProfileMediaLibrary(userId, {
    audience: timelineAudience,
    enabled: Boolean(userId),
  });

  useEffect(() => {
    if (!userId) {
      friendsRequestRef.current += 1;
      setFriends([]);
      setFriendsLoaded(false);
      return undefined;
    }
    if (isPublicPreview) {
      friendsRequestRef.current += 1;
      setFriends([]);
      setFriendsLoaded(true);
      return undefined;
    }

    const requestId = ++friendsRequestRef.current;
    let cancelled = false;
    setFriends([]);
    setFriendsLoaded(false);
    Promise.resolve()
      .then(() => friendshipService.getUserFriends(userId))
      .then((rows) => {
        if (cancelled || requestId !== friendsRequestRef.current) return;
        setFriends(Array.isArray(rows) ? rows : []);
        setFriendsLoaded(true);
      })
      .catch((error) => {
        if (cancelled || requestId !== friendsRequestRef.current) return;
        console.warn('Failed to load profile friends for preview.', error);
        setFriends([]);
        setFriendsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isPublicPreview, userId]);

  useEffect(() => {
    if (!userId || isPublicPreview) {
      followersRequestRef.current += 1;
      setFollowers([]);
      setFollowersLoaded(!userId ? false : true);
      return undefined;
    }
    if (!canLoadProfileFollowers) {
      followersRequestRef.current += 1;
      setFollowers([]);
      setFollowersLoaded(!isProfileFollowerListResolutionPending);
      return undefined;
    }

    const requestId = ++followersRequestRef.current;
    let cancelled = false;
    setFollowers([]);
    setFollowersLoaded(false);
    Promise.resolve()
      .then(() => userService.getUserFollowersPage(userId, { limit: 24 }))
      .then((page) => {
        if (cancelled || requestId !== followersRequestRef.current) return;
        setFollowers(Array.isArray(page?.items) ? page.items : []);
        setFollowersLoaded(true);
      })
      .catch((error) => {
        if (cancelled || requestId !== followersRequestRef.current) return;
        console.warn('Failed to load profile followers for preview.', error);
        setFollowers([]);
        setFollowersLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    canLoadProfileFollowers,
    followersRefreshNonce,
    isProfileFollowerListResolutionPending,
    isPublicPreview,
    userId,
  ]);

  const loadProfileFriendsPage = useCallback((options = {}) => (
    userId ? friendshipService.getUserFriendsPage(userId, options) : Promise.resolve(EMPTY_CONNECTION_PAGE)
  ), [userId]);

  const loadProfileFollowersPage = useCallback((options = {}) => (
    userId ? userService.getUserFollowersPage(userId, options) : Promise.resolve(EMPTY_CONNECTION_PAGE)
  ), [userId]);

  useEffect(() => {
    if (standalone || !portalRoot) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [portalRoot, standalone]);

  useEffect(() => {
    if (standalone) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, standalone]);

  useEffect(() => {
    const requestId = postsTimelineRequestRef.current + 1;
    postsTimelineRequestRef.current = requestId;
    if (!userId) {
      syncPosts([]);
      setHasMorePosts(false);
      setPostsLoading(false);
      postsTimelineLoadRef.current = null;
      postsTimelineUserIdRef.current = null;
      postsTimelineAudienceRef.current = null;
      return undefined;
    }
    postsTimelineLoadRef.current = null;
    postsTimelineUserIdRef.current = null;
    postsTimelineAudienceRef.current = null;
    setHasMorePosts(false);
    setIsLoadingMorePosts(false);
    setPostsLoading(true);
    setPostsError(null);
    const createTimelineLoad = timelineAudience === 'public'
      ? timelineService.createPublicUserTimelineLoad
      : timelineService.createUserTimelineLoad;
    createTimelineLoad(userId, PROFILE_POST_TIMELINE_OPTIONS)
      .then((timelineLoad) => {
        if (postsTimelineRequestRef.current !== requestId) {
          return;
        }
        postsTimelineLoadRef.current = timelineLoad;
        postsTimelineUserIdRef.current = userId;
        postsTimelineAudienceRef.current = timelineAudience;
        syncPosts(Array.isArray(timelineLoad.items) ? timelineLoad.items : []);
        setHasMorePosts(timelineLoad.hasMore === true);
        setPostsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load user posts:', error);
        if (postsTimelineRequestRef.current === requestId) {
          setPostsError('Unable to load posts at the moment.');
          syncPosts([]);
          setHasMorePosts(false);
          postsTimelineLoadRef.current = null;
          postsTimelineUserIdRef.current = null;
          postsTimelineAudienceRef.current = null;
          setPostsLoading(false);
        }
      });
    return () => {
      if (postsTimelineRequestRef.current === requestId) {
        postsTimelineRequestRef.current += 1;
      }
    };
  }, [timelineAudience, userId, syncPosts]);

  const loadMorePosts = useCallback(async () => {
    const timelineLoad = postsTimelineLoadRef.current;
    if (
      !timelineLoad
      || typeof timelineLoad.loadNextPage !== 'function'
      || postsTimelineUserIdRef.current !== userId
      || postsTimelineAudienceRef.current !== timelineAudience
      || !hasMorePosts
      || isLoadingMorePosts
    ) {
      return;
    }

    const requestId = postsTimelineRequestRef.current;
    setIsLoadingMorePosts(true);
    try {
      await timelineLoad.loadNextPage((applyUpdate) => {
        if (postsTimelineRequestRef.current === requestId) {
          return syncPosts(applyUpdate);
        }
        return postsRef.current;
      });
      if (postsTimelineRequestRef.current === requestId) {
        syncPosts(Array.isArray(timelineLoad.items) ? timelineLoad.items : []);
        setHasMorePosts(timelineLoad.hasMore === true);
      }
    } catch (error) {
      if (postsTimelineRequestRef.current === requestId) {
        console.error('Failed to load more user timeline entities:', error);
      }
    } finally {
      if (postsTimelineRequestRef.current === requestId) {
        setIsLoadingMorePosts(false);
      }
    }
  }, [hasMorePosts, isLoadingMorePosts, syncPosts, timelineAudience, userId]);

  const showMediaToast = useCallback((message, type = 'info') => {
    setMediaToast({ id: `${Date.now()}-${Math.random()}`, message, type });
  }, []);

  const clearMediaToast = useCallback(() => {
    setMediaToast(null);
  }, []);

  const handleFollowClick = useCallback(async () => {
    if (!normalizedProfileUserId || !canFollowProfileTarget || isFollowSelfProfile || isFollowActionInFlight) {
      return;
    }

    const requestTarget = {
      userId: normalizedProfileUserId,
      viewerId: normalizedViewerId,
      isPublicPreview,
    };
    const isCurrentFollowTarget = () => {
      const currentTarget = followTargetRef.current;
      return currentTarget.userId === requestTarget.userId
        && currentTarget.viewerId === requestTarget.viewerId
        && currentTarget.isPublicPreview === requestTarget.isPublicPreview;
    };

    setIsFollowActionInFlight(true);
    try {
      const response = followStatus.following
        ? await userService.unfollowUser(normalizedProfileUserId)
        : await userService.followUser(normalizedProfileUserId);
      if (isCurrentFollowTarget()) {
        applyFollowStatus(response);
        setFollowersRefreshNonce((currentValue) => currentValue + 1);
      }
    } catch (error) {
      if (isCurrentFollowTarget()) {
        console.warn('Failed to update profile follow status.', error);
        showMediaToast(t('home.profileOverview.follow.updateFailed', 'Unable to update follow status.'), 'error');
      }
    } finally {
      if (isCurrentFollowTarget()) {
        setIsFollowActionInFlight(false);
      }
    }
  }, [
    canFollowProfileTarget,
    followStatus.following,
    isFollowActionInFlight,
    isFollowSelfProfile,
    isPublicPreview,
    normalizedProfileUserId,
    normalizedViewerId,
    applyFollowStatus,
    showMediaToast,
    t,
  ]);

  const handleTrackPlaybackError = useCallback((message) => {
    showMediaToast(message, 'error');
  }, [showMediaToast]);

  const applyReportedPlaysCount = useCallback((nextCount, track, response) => {
    const profilePlaysCount = normalizeTrackPlaysCount(response?.profilePlaysCount ?? nextCount);
    if (profilePlaysCount !== null) {
      setProfileSummary((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          profile: {
            ...(current.profile || {}),
            playsCount: profilePlaysCount,
          },
        };
      });
    }
    setProfileTracks((current) => (
      applyTrackPlaysCountByIdentity(current, track, response?.trackPlaysCount)
    ));
  }, [setProfileSummary, setProfileTracks]);

  const playbackProfileName = useMemo(() => {
    const displayName = profileSummary?.profile?.displayName;
    if (typeof displayName === 'string' && displayName.trim()) {
      return displayName.trim();
    }
    return resolvedUsername || profileUsername || t('home.editorial.preview.fallbackName', 'This artist');
  }, [profileSummary?.profile?.displayName, profileUsername, resolvedUsername, t]);
  const resolvedProfilePicture = useMemo(() => {
    const summaryAvatar = profileSummary?.profile?.avatar || null;
    if (isPublicPreview) {
      const summaryAvatarValue = getMediaUrlValue(summaryAvatar);
      const canUseSummaryAvatarFallback = !isPublicProfilePictureLookupSettled
        || (
          isAbsoluteHttpUrl(summaryAvatarValue)
          && !isProfilePictureMediaUrl(summaryAvatarValue)
        );
      const summaryAvatarFallback = canUseSummaryAvatarFallback
        ? toPublicProfilePictureUrl(summaryAvatarValue)
        : null;
      return publicProfilePictureUrl
        || summaryAvatarFallback;
    }
    return profilePicture || summaryAvatar;
  }, [
    isPublicPreview,
    isPublicProfilePictureLookupSettled,
    profilePicture,
    profileSummary?.profile?.avatar,
    publicProfilePictureUrl,
  ]);
  const resolvedProfileCover = useMemo(() => {
    const summaryCover = profileSummary?.profile?.cover || null;
    if (isPublicPreview) {
      const summaryCoverFallback = isPublicProfileCoverLookupSettled
        ? null
        : toPublicProfileCoverUrl(getMediaUrlValue(summaryCover));
      return publicProfileCoverUrl
        || summaryCoverFallback;
    }
    return profileCover || summaryCover;
  }, [
    isPublicPreview,
    isPublicProfileCoverLookupSettled,
    profileCover,
    profileSummary?.profile?.cover,
    publicProfileCoverUrl,
  ]);
  const shouldRenderSupportAvatar = !isPublicPreview && shouldUseSupportAvatar;
  const playbackProfileImage = useMemo(() => (
    typeof resolvedProfilePicture === 'string' ? resolvedProfilePicture : resolvedProfilePicture?.url || null
  ), [resolvedProfilePicture]);

  const {
    playingTrackId,
    trackProgressById,
    handlePlayTrack,
    handleSeekTrack,
  } = useProfileTrackPlayback({
    userId,
    audience: timelineAudience,
    tracks: profileTracks,
    onPlaysCountChange: applyReportedPlaysCount,
    onError: handleTrackPlaybackError,
    errorMessage: t('home.editorial.media.trackPlayFailed', 'Unable to play this track.'),
    sourceLabel: playbackProfileName,
    sourceSubtitle: t('home.editorial.preview.tracksMeta', 'Demos and live takes'),
    sourceImage: playbackProfileImage,
  });

  const isPostsTimelineCurrent = Boolean(userId)
    && postsTimelineUserIdRef.current === userId
    && postsTimelineAudienceRef.current === timelineAudience;
  const shouldSuppressStalePosts = !userId || !isPostsTimelineCurrent;
  const visibleTimelineItems = useMemo(
    () => (shouldSuppressStalePosts ? [] : posts),
    [posts, shouldSuppressStalePosts]
  );
  const visiblePosts = useMemo(
    () => visibleTimelineItems.filter(isPostTimelineItem),
    [visibleTimelineItems]
  );
  const canLoadMorePosts = isPostsTimelineCurrent && hasMorePosts;
  const shouldShowFriendsSection = !isPublicPreview;

  const {
    profileDisplayName,
    handle,
    instrumentExperiences,
    skills,
    mainInstrument,
    seekingDetails,
    commitmentDetails,
    weekSchedule,
    influencesList,
    equipmentList,
    locationLabel,
    seekingHeroLabel,
    seekingGenres,
    seekingQuote,
    statsItems,
    heroTopJoinedLabel,
    mediaTracks,
    mediaPhotos,
  } = useMemo(() => buildProfileEditorialViewModel({
    profileSummary,
    username: resolvedUsername || '',
    displayNameFallback: t('home.editorial.preview.fallbackName', 'This artist'),
    posts: visiblePosts,
    followersCount: followStatus.followerCount,
    includePlaysStat: true,
    includeFollowersStat: !isPublicPreview,
    bandMemberships: canLoadBandMemberships && hasLoadedBandMemberships ? bandMemberships : null,
    includeBandsStat: true,
    friends,
    profileTracks,
    userMediaPhotos,
    trackProgressById,
    playingTrackId,
    includeFriendsStat: shouldShowFriendsSection,
    includeEventsStat: false,
    t,
    locale,
  }), [
    bandMemberships,
    canLoadBandMemberships,
    followStatus.followerCount,
    friends,
    hasLoadedBandMemberships,
    isPublicPreview,
    shouldShowFriendsSection,
    locale,
    playingTrackId,
    profileSummary,
    profileTracks,
    resolvedUsername,
    t,
    trackProgressById,
    userMediaPhotos,
    visiblePosts,
  ]);

  const shouldShowSeekingSection = Boolean(seekingDetails);
  const tabs = useMemo(() => buildProfileEditorialTabs(t, sectionIds, {
    includeFriends: shouldShowFriendsSection,
    includeSeeking: shouldShowSeekingSection,
  }), [sectionIds, shouldShowFriendsSection, shouldShowSeekingSection, t]);

  useEffect(() => {
    if (!shouldShowSeekingSection && activeSection === sectionIds.SEEKING) {
      setActiveSection(sectionIds.INFO);
    }
    if (!shouldShowFriendsSection && activeSection === sectionIds.FRIENDS) {
      setActiveSection(sectionIds.INFO);
    }
  }, [
    activeSection,
    sectionIds.FRIENDS,
    sectionIds.INFO,
    sectionIds.SEEKING,
    shouldShowFriendsSection,
    shouldShowSeekingSection,
  ]);
  const connectButtonLabel = useMemo(() => {
    if (friendshipRelationship === 'friend') {
      return t('friendsPage.userList.relationships.friend', 'Connected');
    }
    if (friendshipRelationship === 'incoming') {
      return t('friendsPage.userList.relationships.incoming', 'Respond');
    }
    if (friendshipRelationship === 'outgoing') {
      return t('friendsPage.userList.relationships.outgoing', 'Pending');
    }
    return t('friendsPage.userList.actions.connect', 'Connect');
  }, [friendshipRelationship, t]);
  const shouldRenderContactActions = Boolean(userId && viewerId && !isPublicPreview && !isContactSelfProfile);
  const publicAuthAction = isPublicPreview ? <PublicAuthAction /> : null;
  const publicProfileMessageAction = useMemo(() => {
    if (!isPublicPreview) {
      return null;
    }

    return (
      <a className="home-hero__btn home-hero__btn--ghost" href={PUBLIC_AUTH_ACTION_HREF}>
        <MessageIcon width="16" height="16" />
        {t('auth.publicCta.message', 'Message')}
      </a>
    );
  }, [isPublicPreview, t]);
  const profileContactActions = useMemo(() => {
    if (!shouldRenderContactActions) {
      return null;
    }

    const messageLabel = isMessageInFlight
      ? t('friendsPage.userList.actions.opening', 'Opening...')
      : t('friendsPage.userList.actions.message', 'Message');
    const followLabel = isFollowActionInFlight
      ? (followStatus.following
        ? t('friendsPage.userList.actions.unfollowing', 'Unfollowing...')
        : t('friendsPage.userList.actions.followingBusy', 'Following...'))
      : (followStatus.following
        ? t('friendsPage.userList.actions.following', 'Following')
        : t('friendsPage.userList.actions.follow', 'Follow'));
    const followClassName = followStatus.following
      ? 'home-hero__btn home-hero__btn--ghost'
      : 'home-hero__btn home-hero__btn--accent';
    const connectLabel = isConnectInFlight
      ? t('friendsPage.userList.actions.connecting', 'Connecting...')
      : connectButtonLabel;
    const connectClassName = friendshipRelationship === 'none'
      ? 'home-hero__btn home-hero__btn--accent'
      : 'home-hero__btn home-hero__btn--ghost';

    return (
      <>
        {canFollowProfileTarget ? (
          <button
            type="button"
            className={followClassName}
            onClick={handleFollowClick}
            disabled={isFollowActionInFlight}
            aria-busy={isFollowActionInFlight || undefined}
          >
            <FollowIcon width="16" height="16" />
            {followLabel}
          </button>
        ) : null}
        <button
          type="button"
          className={connectClassName}
          onClick={handleConnectClick}
          disabled={isConnectDisabled}
          aria-busy={isConnectInFlight || undefined}
        >
          <ConnectIcon width="16" height="16" />
          {connectLabel}
        </button>
        <button
          type="button"
          className="home-hero__btn home-hero__btn--ghost"
          onClick={handleMessageClick}
          disabled={isMessageDisabled}
          aria-busy={isMessageInFlight || undefined}
        >
          <MessageIcon width="16" height="16" />
          {messageLabel}
        </button>
      </>
    );
  }, [
    canFollowProfileTarget,
    connectButtonLabel,
    followStatus.following,
    friendshipRelationship,
    handleFollowClick,
    handleConnectClick,
    handleMessageClick,
    isConnectDisabled,
    isConnectInFlight,
    isFollowActionInFlight,
    isMessageDisabled,
    isMessageInFlight,
    shouldRenderContactActions,
    t,
  ]);
  const metaError = useMemo(() => {
    if (!userId) {
      return t('home.profileOverview.errors.missingUser', 'Missing user identifier.');
    }
    if (profileError) {
      return t('home.profileOverview.errors.loadFailed', 'Unable to load user details right now.');
    }
    return null;
  }, [profileError, t, userId]);

  if (!standalone && !portalRoot) {
    return null;
  }

  const overviewNode = (
    <div
      className={classNames('profile-overview', standalone && 'profile-overview--standalone')}
      role={standalone ? undefined : 'dialog'}
      aria-modal={standalone ? undefined : true}
    >
      {standalone ? null : <div className="profile-overview__backdrop" onClick={onClose} />}
      <div className={classNames('profile-overview__modal', standalone && 'profile-overview__modal--standalone')}>
        {standalone ? null : (
          <CloseButton className="profile-overview__close" ariaLabel={t('common.close', 'Close profile')} onClick={onClose} />
        )}
        <ProfileEditorialView
          className="profile-overview__content"
          preContent={(
            <>
              {metaError ? (
                <div className="profile-overview__meta-error">{metaError}</div>
              ) : null}
              {profileShareToast && (
                <Notification
                  key={profileShareToast.id}
                  message={profileShareToast.message}
                  type={profileShareToast.type}
                  duration={3200}
                  onClose={clearProfileShareToast}
                />
              )}
              {mediaToast && (
                <Notification
                  key={mediaToast.id}
                  message={mediaToast.message}
                  type={mediaToast.type}
                  duration={3200}
                  onClose={clearMediaToast}
                />
              )}
            </>
          )}
          tabs={tabs}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          tabbarAriaLabel={t('home.editorial.tabbarAria', 'Profile sections')}
          tabbarActions={publicAuthAction}
          tabbarShowActionsOnMobile={Boolean(publicAuthAction)}
          heroProps={{
            id: sectionIds.INFO,
            username: profileDisplayName,
            handle,
            tagline: profileSummary?.profile?.about,
            avatarUrl: shouldRenderSupportAvatar ? null : resolvedProfilePicture,
            coverImageUrl: resolvedProfileCover,
            isOnline: isProfileOnline,
            presenceStatus: heroPresenceStatus,
            presenceLabel: heroPresenceLabel,
            onlineLabel: heroOnlineLabel,
            joinedLabel: heroTopJoinedLabel,
            locationLabel,
            playsLabel: mainInstrument || t('home.editorial.hero.instrumentMissing', 'Instrument not selected'),
            basedIn: locationLabel,
            preferredStyle: profileSummary?.profile?.preferredStyle,
            preferredStyles: profileSummary?.profile?.preferredStyles,
            mainInstrument,
            seekingValue: seekingHeroLabel,
            seekingHref: `#${sectionIds.SEEKING}`,
            seekingHeading: t('home.editorial.hero.seekingHeading', 'Seeking'),
            basedInLabel: t('home.editorial.hero.basedIn', 'Based in'),
            preferredStyleLabel: t('home.editorial.snapshot.preferredStyle', 'Preferred style'),
            preferredStyleEmptyLabel: t(
              'home.profileHeader.preferredStyle.noResults',
              PROFILE_PREFERRED_STYLE_EMPTY_LABEL,
            ),
            preferredStylePlaceholder: t(
              'home.editorial.snapshot.preferredStylePlaceholder',
              PROFILE_PREFERRED_STYLE_INPUT_PLACEHOLDER,
            ),
            preferredStyleClearLabel: t('home.profileHeader.preferredStyle.clear', 'Clear preferred style'),
            mainInstrumentLabel: t('home.editorial.snapshot.mainInstrument', 'Main instrument'),
            emptyPlaceholder: t('home.editorial.snapshot.empty', 'Not set'),
            mainInstrumentHref: `#${sectionIds.SOUND}`,
            shareLabel: t('home.editorial.hero.share', 'Share profile'),
            onShareProfile: profileShareAction,
            extraActions: publicProfileMessageAction || profileContactActions,
            useDefaultAvatarFallback: isPublicPreview,
            onAvatarClick: resolvedProfilePicture ? () => setShowProfilePicturePreview(true) : undefined,
            avatarPreviewLabel: t('home.editorial.hero.avatarPreview', 'View profile picture'),
            statsItems,
          }}
          seekingProps={shouldShowSeekingSection ? {
            id: sectionIds.SEEKING,
            eyebrow: t('home.editorial.seeking.eyebrow', 'Seeking'),
            title: seekingDetails ? (
              <>
                {seekingDetails.type === 'band'
                  ? t('home.editorial.seeking.titleBand', 'Looking for a band.')
                  : t('home.editorial.seeking.titleMembers', 'Looking for members.')}
                {seekingDetails.openToJam ? (
                  <>
                    <br />
                    <em>{t('home.editorial.seeking.titleSub', 'Open to a jam.')}</em>
                  </>
                ) : null}
              </>
            ) : t('home.editorial.preview.seekingTitleEmpty', 'No seeking details yet.'),
            side: t('home.editorial.seeking.side', 'Visible to anyone on Muzone. Helps the right people find you - bands recruiting, musicians forming projects, friends booking gigs.'),
            lookingForLabel: t('home.editorial.seeking.lookingFor', 'Looking for'),
            bandLabel: t('home.editorial.seeking.lookingForBand', 'Band'),
            membersLabel: t('home.editorial.seeking.lookingForMembers', 'Members'),
            noneLabel: t('home.editorial.seeking.lookingForNone', 'Not looking'),
            orLabel: t('home.editorial.seeking.or', 'or'),
            seekingType: seekingDetails?.type,
            openToJam: seekingDetails?.openToJam !== false,
            openToJamLabel: t('home.editorial.seeking.openToJam', "I'm open to a jam"),
            bandStyles: seekingDetails?.bandStyles || [],
            members: seekingDetails?.members || [],
            membersChipLabel: t('home.editorial.seeking.lookingForRoles', 'Looking for'),
            bandStylesViewLabel: t('home.editorial.seeking.preferredBandStyle', 'Preferred band style'),
            stylesEditorLabel: t('home.editorial.seeking.stylesEditorLabel', 'Styles you are open to'),
            membersEditorLabel: t('home.editorial.seeking.membersEditorLabel', 'Roles you need'),
            quote: seekingQuote,
            genresLabel: t('home.editorial.seeking.alsoOpen', 'Looking for'),
            genres: seekingGenres,
            emptyTitle: t('home.editorial.preview.seekingEmptyTitle', 'No seeking details yet'),
            emptyDescription: t('home.editorial.preview.seekingEmptyDescription', 'This profile has not shared what they are looking for.'),
          } : null}
          soundProps={{
            id: sectionIds.SOUND,
            eyebrow: t('home.editorial.sound.eyebrow', 'Sound & gear'),
            title: (
              <>
                {t('home.editorial.sound.titleA', 'The signal chain.')}{' '}
                <em>{t('home.editorial.sound.titleB', 'What I plug in, who I steal from.')}</em>
              </>
            ),
            side: null,
            influencesTitle: t('home.editorial.sound.influences', 'Influences'),
            influences: influencesList,
            influencesHint: influencesList.length === 0 ? null : t('home.editorial.sound.influencesHint', ''),
            equipmentTitle: t('home.editorial.sound.equipment', 'Equipment'),
            equipment: equipmentList,
            equipmentHint: equipmentList.length === 0 ? null : t('home.editorial.sound.equipmentHint', ''),
            skillsTitle: t('home.editorial.sound.instrument', 'Instrument experience'),
            skills,
            emptyTitle: t('home.editorial.preview.soundEmptyTitle', 'No sound details yet'),
            emptyDescription: t('home.editorial.preview.soundEmptyDescription', 'Influences, gear, and instrument experience have not been shared.'),
            instrumentExperiences,
          }}
          commitmentProps={{
            id: sectionIds.COMMITMENT,
            eyebrow: t('home.editorial.commitment.eyebrow', 'Commitment'),
            title: (
              <>
                {t('home.editorial.commitment.titleA', 'When I am free.')}{' '}
                <em>{t('home.editorial.commitment.titleB', 'And when I am not.')}</em>
              </>
            ),
            side: t('home.editorial.commitment.side', 'Bandmates see this when they pitch a rehearsal. Updates here update the calendar everywhere on Muzone.'),
            schedule: weekSchedule,
            weekAriaLabel: t('home.editorial.commitment.weekAria', 'Weekly availability'),
            notesLabel: t('home.editorial.commitment.notesLabel', 'Additional notes'),
            notesValue: commitmentDetails.notes
              ? commitmentDetails.notes
              : <em>{t('home.editorial.commitment.noNotes', 'No additional notes yet.')}</em>,
            notesHint: t('home.editorial.commitment.notesHint', 'Outside the slots above, you can usually be flexible with one week notice - especially for one-off gigs, last-minute fill-ins, or weekend tracking sessions.'),
            commitmentDetails,
          }}
          bandsProps={{
            id: sectionIds.BANDS,
            eyebrow: t('home.editorial.bands.eyebrow', 'Bands'),
            title: (
              <>
                {t('home.editorial.bands.titleA', 'The projects')}{' '}
                <em>{t('home.editorial.bands.titleB', 'I run.')}</em>
              </>
            ),
            side: t('home.editorial.bands.side', 'Bands you own or play in. Each card opens the band page with roster, posts, and tour dates.'),
            bands: canLoadBandMemberships ? (bandMemberships || []) : [],
            currentUserId: viewerId,
            ownerLabel: t('home.editorial.bands.owner', 'Owner'),
            publicMedia: isPublicPreview,
            emptyTitle: isBandMembershipLoading
              ? t('home.editorial.bands.loadingTitle', 'Loading bands...')
              : t('home.editorial.preview.bandsEmptyTitle', 'No public bands yet'),
            emptyDescription: t('home.editorial.preview.bandsEmptyDescription', 'Bands are only shown here when they are available to this viewer.'),
          }}
          galleryProps={{
            id: sectionIds.GALLERY,
            eyebrow: t('home.editorial.gallery.eyebrow', 'Gallery'),
            title: (
              <>
                {t('home.editorial.gallery.titleA', 'Photos')}{' '}
                <em>{t('home.editorial.gallery.titleB', 'from the room.')}</em>
              </>
            ),
            side: t('home.editorial.gallery.side', 'Photos uploaded directly to the profile media library.'),
            photos: mediaPhotos,
            photosLabel: t('home.editorial.gallery.photos', 'Photos'),
            photosMeta: t('home.editorial.preview.photosMeta', 'Profile media library'),
            emptyPhotosTitle: t('home.editorial.gallery.photosEmptyTitle', 'No photos yet'),
            emptyPhotosDescription: t('home.editorial.preview.photosEmptyDescription', 'No profile media photos have been uploaded yet.'),
            previousPhotosLabel: t('home.editorial.gallery.previousPhotos', 'Scroll photos left'),
            nextPhotosLabel: t('home.editorial.gallery.nextPhotos', 'Scroll photos right'),
          }}
          mediaProps={{
            id: sectionIds.MEDIA,
            eyebrow: t('home.editorial.media.eyebrow', 'Media'),
            title: (
              <>
                {t('home.editorial.media.titleA', 'Tracks')}{' '}
                <em>{t('home.editorial.media.titleB', 'and live takes.')}</em>
              </>
            ),
            side: t('home.editorial.media.side', 'Demos and audio uploaded directly to the profile media library.'),
            tracks: mediaTracks,
            tracksLabel: t('home.editorial.media.tracks', 'Tracks'),
            tracksMeta: t('home.editorial.preview.tracksMeta', 'Demos and live takes'),
            emptyTracksTitle: t('home.editorial.media.tracksEmptyTitle', 'No tracks yet'),
            emptyTracksDescription: t('home.editorial.preview.tracksEmptyDescription', 'No demos or live takes have been uploaded yet.'),
            onPlayTrack: handlePlayTrack,
            onSeekTrack: handleSeekTrack,
          }}
          friendsProps={shouldShowFriendsSection ? {
            id: sectionIds.FRIENDS,
            eyebrow: t('home.editorial.connections.eyebrow', 'Connections'),
            title: t('home.editorial.friends.title', 'Friends'),
            side: t('home.editorial.friends.side', 'Friend requests appear in your inbox. Add people from your band rosters or by handle.'),
            friends,
            messageLabel: t('home.editorial.friends.message', 'Message'),
            showInviteRow: false,
            emptyTitle: friendsLoaded
              ? t('home.editorial.preview.friendsEmptyTitle', 'No public friends yet')
              : t('home.editorial.friends.loadingTitle', 'Loading friends...'),
            emptyDescription: t('home.editorial.preview.friendsEmptyDescription', 'Friends are shown when available to this viewer.'),
            autoScrollDirection: 'right',
            viewAll: {
              label: t('home.editorial.connections.viewAll', 'View All'),
              ariaLabel: t('home.editorial.friends.viewAllAria', 'View all friends'),
              modalTitle: t('home.editorial.friends.modalTitle', 'Friends'),
              modalEyebrow: t('home.editorial.connections.eyebrow', 'Connections'),
              modalAriaLabel: t('home.editorial.friends.modalAria', 'Friends'),
              loadPage: loadProfileFriendsPage,
              disabled: !userId || !friendsLoaded,
              emptyTitle: t('home.editorial.preview.friendsEmptyTitle', 'No public friends yet'),
              emptyDescription: t('home.editorial.preview.friendsEmptyDescription', 'Friends are shown when available to this viewer.'),
              loadingTitle: t('home.editorial.friends.loadingTitle', 'Loading friends...'),
              errorTitle: t('home.editorial.friends.loadErrorTitle', 'Unable to load friends'),
              errorDescription: t('home.editorial.friends.loadErrorDescription', 'Friends could not be loaded right now.'),
              retryLabel: t('home.editorial.connections.retry', 'Try again'),
              loadMoreLabel: t('home.editorial.connections.loadMore', 'Load more'),
              loadingMoreLabel: t('home.editorial.connections.loadingMore', 'Loading...'),
            },
            additionalGroups: [{
              id: 'followers',
              title: t('home.editorial.followers.title', 'Followers'),
              people: followers,
              showInviteRow: false,
              emptyTitle: followersLoaded
                ? t('home.editorial.followers.emptyTitle', 'No followers yet')
                : t('home.editorial.followers.loadingTitle', 'Loading followers...'),
              emptyDescription: t('home.editorial.preview.followersEmptyDescription', 'Followers are shown when available to this viewer.'),
              previousLabel: t('home.editorial.followers.previousFollowers', 'Scroll followers left'),
              nextLabel: t('home.editorial.followers.nextFollowers', 'Scroll followers right'),
              autoScrollDirection: 'left',
              viewAll: canLoadProfileFollowers ? {
                label: t('home.editorial.connections.viewAll', 'View All'),
                ariaLabel: t('home.editorial.followers.viewAllAria', 'View all followers'),
                modalTitle: t('home.editorial.followers.modalTitle', 'Followers'),
                modalEyebrow: t('home.editorial.connections.eyebrow', 'Connections'),
                modalAriaLabel: t('home.editorial.followers.modalAria', 'Followers'),
                loadPage: loadProfileFollowersPage,
                disabled: !userId || !followersLoaded,
                emptyTitle: t('home.editorial.followers.emptyTitle', 'No followers yet'),
                emptyDescription: t('home.editorial.preview.followersEmptyDescription', 'Followers are shown when available to this viewer.'),
                loadingTitle: t('home.editorial.followers.loadingTitle', 'Loading followers...'),
                errorTitle: t('home.editorial.followers.loadErrorTitle', 'Unable to load followers'),
                errorDescription: t('home.editorial.followers.loadErrorDescription', 'Followers could not be loaded right now.'),
                retryLabel: t('home.editorial.connections.retry', 'Try again'),
                loadMoreLabel: t('home.editorial.connections.loadMore', 'Load more'),
                loadingMoreLabel: t('home.editorial.connections.loadingMore', 'Loading...'),
              } : null,
            }],
          } : null}
          postsProps={{
            id: sectionIds.POSTS,
            eyebrow: t('home.editorial.posts.eyebrow', 'Posts'),
            title: (
              <>
                {t('home.editorial.posts.titleA', 'What they have been')}{' '}
                <em>{t('home.editorial.posts.titleB', 'putting out there.')}</em>
              </>
            ),
            side: t('home.editorial.posts.side', 'Pinned post stays at the top of your public profile. Drafts and replies live in your inbox.'),
            showSide: false,
            posts: visiblePosts,
            currentUserId: isPublicPreview ? null : viewerId,
            readOnly: isPublicPreview,
            allowComposer: false,
            hasMorePosts: canLoadMorePosts,
            isLoadingMorePosts,
            loadMorePosts,
            publicMedia: isPublicPreview,
            loadMoreLabel: t('home.editorial.posts.loadMore', 'Load more posts'),
            loadingMoreLabel: t('home.editorial.posts.loadingMore', 'Loading posts...'),
            emptyTitle: postsLoading
              ? t('home.editorial.posts.loadingTitle', 'Loading posts...')
              : t('home.editorial.posts.emptyTitle', 'No posts yet'),
            emptyDescription: postsError || t('home.editorial.preview.postsEmptyDescription', 'This profile has not posted anything yet.'),
          }}
        />
      </div>
      {showProfilePicturePreview && (
        <ProfilePicturePreviewModal
          src={resolvedProfilePicture}
          useSupportAvatar={shouldRenderSupportAvatar}
          onClose={() => setShowProfilePicturePreview(false)}
        />
      )}
    </div>
  );

  if (standalone) {
    return overviewNode;
  }

  // As an overlay, ProfileOverview is rendered inline in page content, so it
  // sits under MainPage's MobileHeaderSlotContext.Provider and would otherwise
  // portal its editorial SectionTabbar into — and hijack — the global mobile
  // app header, leaving the dialog without its section navigation. Override the
  // slot to null so the tabbar renders inside the dialog instead. (The
  // standalone/home usage above keeps inheriting the slot, so its tabbar still
  // lands in the header as intended.)
  return ReactDOM.createPortal(
    <MobileHeaderSlotContext.Provider value={null}>
      {overviewNode}
    </MobileHeaderSlotContext.Provider>,
    portalRoot,
  );
}
